import ApiClient from '@src/lib/api_client'
import { API } from '@src/lib/api_path'
import SessionManager from '@src/lib/session_manager'

type FavoriteExercisesResponse = {
  favorite_exercise_ids: number[]
}

/**
 * Service for managing club favorite exercises (trainers only)
 */
export class FavoriteExerciseService {
  apiClient: ApiClient = new ApiClient()

  /**
   * Get list of favorite exercise IDs for current club
   * Note: Only trainers have access to this endpoint
   * NOTE: This is now OPTIONAL - backend should include is_favorite on each exercise in GET /exercises
   * This method exists only as a fallback or for separate loading if needed
   */
  getFavoriteExercises (): Promise<number[]> {
    if (!SessionManager.Instance.isManager()) {
      // Members don't have access to the favorites endpoint
      // Favorites come directly on exercise objects with is_favorite field
      return Promise.resolve([])
    }

    const endpoint = API.TRAINERS_API.FAVORITE_EXERCISES

    return this.apiClient.get<FavoriteExercisesResponse>(endpoint)
      .then(response => Promise.resolve(response.favorite_exercise_ids || []))
      .catch(error => {
        console.warn('⚠️ Could not load favorites (will use is_favorite from exercises instead):', error.message)
        // Return empty array instead of rejecting - this prevents redirect to login
        // Favorites will come from is_favorite field on each exercise object
        return Promise.resolve([])
      })
  }

  /**
   * Add exercise to club favorites
   * Note: Only trainers can perform this action
   */
  addFavorite (exerciseId: number): Promise<void> {
    if (!SessionManager.Instance.isManager()) {
      return Promise.reject(new Error('Only trainers can add favorites'))
    }

    const endpoint = API.TRAINERS_API.FAVORITE_EXERCISE.replace('{exerciseId}', String(exerciseId))

    return this.apiClient.post(endpoint, {})
      .then(() => Promise.resolve())
      .catch(error => {
        console.error('Failed to add favorite exercise', error)
        return Promise.reject(error)
      })
  }

  /**
   * Remove exercise from club favorites
   * Note: Only trainers can perform this action
   */
  removeFavorite (exerciseId: number): Promise<void> {
    if (!SessionManager.Instance.isManager()) {
      return Promise.reject(new Error('Only trainers can remove favorites'))
    }

    const endpoint = API.TRAINERS_API.FAVORITE_EXERCISE.replace('{exerciseId}', String(exerciseId))

    return this.apiClient.delete(endpoint)
      .then(() => Promise.resolve())
      .catch(error => {
        console.error('Failed to remove favorite exercise', error)
        return Promise.reject(error)
      })
  }
}
